/**
 * CaseNotes.tsx — the per-case clinician notes thread (G3).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: G3 (feature-gap closure).
 *
 * WHY: a clinician (or admin) needs a running note thread on an order case,
 * INDEPENDENT of the prescribe/reject decision — "manage it like posts and
 * comments", exactly like the callback ticket's activity log. This reuses that
 * grammar directly: the shared rich-text NoteEditor (admin/callbacks) for
 * add + author-only edit, a chronological (oldest→newest) list with author,
 * timestamp, "· edited" and an author-only Edit link, and the same
 * defence-in-depth `sanitizeNoteHtml` strip before any body is handed to
 * `dangerouslySetInnerHTML` (the SERVER is the real sanitiser).
 *
 * The pinned CaseNote wire carries `authorEmail: null` (PHI-free events, R19),
 * so the display name is resolved best-effort via the identity clinician-record
 * read; an unresolvable id (or a 404) falls back to the raw author id. That
 * SAME read now also carries the author's ROLE onto the row (2026-08-27) —
 * this thread ADMITTED ADMIN as well as CLINICIAN, so a name alone left an
 * admin's note and a prescribing clinician's note indistinguishable in the
 * clinical record; see noteAuthor.tsx for the derivation and its limits. The
 * ADMIN grant was withdrawn on 2026-08-28 and the composer is now hidden from
 * them, but the badge still labels the notes written before that. This is
 * the CLINICAL surface's thread (clinician ReviewView); the admin Payments
 * drawer renders its own PAYMENT notes thread (admin/PaymentNotes.tsx) over
 * the same store — Wave 6 §3's interim `[payment]` marker segregates the two
 * client-side (payment-marked notes are filtered OUT here, so payment chatter
 * never lands on a clinical surface) until the backend grows a real kind.
 */
import { useCallback, useEffect, useState } from 'react';
import { addCaseNote, editCaseNote, getCaseNotes } from '../api/orders';
import type { CaseNote } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { noteByline } from '../lib/noteByline';
import { isPaymentNote } from '../lib/paymentNotes';
import { sanitizeNoteHtml } from '../lib/sanitizeHtml';
import { NoteEditor } from '../admin/callbacks/NoteEditor';
import { actionLinkStyle } from '../admin/callbacks/shared';
import { SectionTitle } from '../staff/ui';
import { Card, ErrorState, Help, Spinner } from '../ui/kit';
import { NoteAuthorLine, useNoteAuthors, type NoteAuthor } from './noteAuthor';

type Loadable<T> = { state: 'loading' | 'ready' | 'error'; data: T | null };

export function CaseNotesSection({ orderCaseId }: { orderCaseId: string }) {
  const { me } = useAuth();
  // WHO MAY WRITE, mirrored from the server (2026-08-28). clinic-service's
  // assessment-notes.controller.ts is @Roles('CLINICIAN') on both the add and
  // the edit route, so an ADMIN's submit now 403s. R41 keeps the SERVER as the
  // control and nothing here weakens that — this only stops the UI OFFERING an
  // action that is guaranteed to fail. Note the edit gate is role AND
  // authorship: a note an admin wrote BEFORE the rule changed is still theirs
  // by `authorUserId`, so authorship alone would keep showing them an Edit
  // button the server refuses.
  const canWriteClinicalNotes = me?.role === 'CLINICIAN';

  const [notes, setNotes] = useState<Loadable<CaseNote[]>>({ state: 'loading', data: null });
  // authorUserId → name AND the role the identity clinician record supports
  // (2026-08-27): this panel is titled "Clinical notes" and its compose route
  // admits ADMIN as well as CLINICIAN, so the name alone left an admin's note
  // indistinguishable from a prescribing clinician's. See noteAuthor.tsx —
  // same hook in AssessmentNotes.tsx, so the two panels (now rendered SIDE BY
  // SIDE on the order-backed Review tab) can never disagree about an author.
  const { authors, resolveAuthors } = useNoteAuthors();

  const load = useCallback(() => {
    setNotes({ state: 'loading', data: null });
    getCaseNotes(orderCaseId)
      .then((list) => {
        // Wave 6 §3 interim segregation: the shared case-notes store also
        // carries PAYMENT notes (leading '[payment]' marker — see
        // lib/paymentNotes.ts). They belong to the admin Payments drawer
        // ONLY — filter them out of the clinical thread so payment chatter
        // never renders on a clinical surface.
        const clinicalNotes = list.items.filter((n) => !isPaymentNote(n.body));
        setNotes({ state: 'ready', data: clinicalNotes });
        resolveAuthors(clinicalNotes);
      })
      .catch(() => setNotes({ state: 'error', data: null }));
  }, [orderCaseId, resolveAuthors]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <SectionTitle>Clinical notes</SectionTitle>
      {notes.state === 'loading' ? (
        <Spinner label="Loading clinical notes…" />
      ) : notes.state === 'error' || !notes.data ? (
        <ErrorState message="Sorry — we could not load the clinical notes." onRetry={load} />
      ) : (
        <>
          {notes.data.length === 0 ? (
            <Help style={{ marginBottom: 'var(--gps-s-3)' }}>No clinical notes yet.</Help>
          ) : (
            <ul aria-label="Clinical notes" style={{ listStyle: 'none', margin: '0 0 var(--gps-s-3)', padding: 0, display: 'grid', gap: '10px' }}>
              {/* Oldest first — sorted defensively even though the API orders
                  oldest→newest — so the thread reads top-down with the newest
                  note beside the composer, like a comment thread. */}
              {[...notes.data]
                .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
                .map((note) => (
                  <CaseNoteRow
                    key={note.noteId}
                    note={note}
                    author={authors[note.authorUserId]}
                    canEdit={canWriteClinicalNotes && note.authorUserId === me?.userId}
                    orderCaseId={orderCaseId}
                    onChanged={load}
                  />
                ))}
            </ul>
          )}
          {canWriteClinicalNotes ? (
            <NoteEditor
            formLabel="Add a clinical note"
            textboxLabel="New clinical note"
            submitLabel="Add note"
            onSubmit={async (html) => {
              await addCaseNote(orderCaseId, html);
              load();
            }}
            />
          ) : (
            // Deliberately says where the admin CAN write rather than only
            // what they cannot: admins and clinicians share the activity log,
            // which is the channel this panel is not.
            <Help>
              Only clinicians can add or edit clinical notes. You have read access here — to share
              something with the clinical team, use the activity log.
            </Help>
          )}
        </>
      )}
    </Card>
  );
}

function CaseNoteRow({
  note,
  author,
  canEdit,
  orderCaseId,
  onChanged,
}: {
  note: CaseNote;
  author?: NoteAuthor;
  canEdit: boolean;
  orderCaseId: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <li>
        <NoteEditor
          formLabel="Edit clinical note"
          textboxLabel="Clinical note text"
          submitLabel="Save note"
          initialHtml={note.body}
          onSubmit={async (html) => {
            await editCaseNote(orderCaseId, note.noteId, html);
            setEditing(false);
            onChanged();
          }}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }
  return (
    <li style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-md)', padding: '10px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>
          <NoteAuthorLine note={note} author={author} />
        </span>
        <span style={{ fontSize: '11px', color: 'var(--gps-muted)' }}>
          {noteByline(note)}
        </span>
      </div>
      {/* Defence in depth (the server is the real sanitiser) — strip to a safe
          tag allowlist before ever rendering the note body as HTML. */}
      <div style={{ fontSize: '14px', color: 'var(--gps-text)' }} dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.body) }} />
      {canEdit ? (
        <button type="button" onClick={() => setEditing(true)} style={{ ...actionLinkStyle, marginTop: '6px' }}>
          Edit
        </button>
      ) : null}
    </li>
  );
}
